// ─────────────────────────────────────────────────────────────────────────────
// KnowledgeBaseService
// ─────────────────────────────────────────────────────────────────────────────
// HTTP service for the Knowledge Base API.
// Uses the Odoo proxy endpoints under /v1/ai/kb/documents.
// ─────────────────────────────────────────────────────────────────────────────

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { delay, map, Observable, of, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  IKbApiDocumentDto,
  IKbApiResponse,
  IKbCustomerScope,
  IKbDeleteDocumentDataDto,
  IKbDocument,
  IKbDocumentMetadataDto,
  IKbDownloadDocumentsDataDto,
  IKbListDocumentsDataDto,
  IKbProductScope,
  IKbUploadResponseDataDto,
  IKbRequestMetadata,
  IKbUpdateRequest,
  IKbUploadRequest,
  KbDocumentStatus,
  KbDocumentType,
  KbScopeLevel,
} from '../models/knowledge-base.models';

@Injectable({ providedIn: 'root' })
export class KnowledgeBaseService {
  private readonly baseUrl = environment.BASE_URL_API + 'ai/kb/documents';
  private readonly useMockData = environment.useMockData;

  constructor(private http: HttpClient) {}

  // ── Read ─────────────────────────────────────────────────────────────────

  /** Fetch all documents. */
  getDocuments(): Observable<IKbDocument[]> {
    if (this.useMockData) {
      return of(this.createMockListResponse()).pipe(
        delay(1000),
        map((response) => this.mapDocumentsResponse(response)),
      );
    }
    return this.http.get<IKbApiResponse<IKbListDocumentsDataDto>>(this.baseUrl).pipe(map((response) => this.mapDocumentsResponse(response)));
  }

  // ── Upload ───────────────────────────────────────────────────────────────

  /** Upload a new document. */
  uploadDocument(request: IKbUploadRequest): Observable<IKbDocument> {
    if (this.useMockData) {
      const mockId = 'mock-' + Date.now().toString(36);
      return of({
        document_id: mockId,
        name: `projects/demo/documents/${mockId}`,
        gcs_uri: `gs://volta-demo-kb/${request.file.name}`,
        metadata: {
          filename: request.file.name,
          file_size: request.file.size,
          upload_date: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          document_type: request.metadata.document_type,
          tags: request.metadata.tags,
          user_comment: request.metadata.user_comment,
        },
        index_status: {
          status: KbDocumentStatus.INDEXED,
          index_time: new Date().toISOString(),
        },
        mime_type: request.file.type,
      } as IKbApiDocumentDto).pipe(
        delay(1500),
        map((doc) => this.mapDocument(doc)),
      );
    }

    const formData = new FormData();
    formData.append('file', request.file);
    formData.append('metadata', JSON.stringify(request.metadata));

    return this.http.post<IKbApiResponse<IKbUploadResponseDataDto>>(this.baseUrl, formData).pipe(
      map((response) => {
        const result = response.data?.results?.[0];
        if (!result || result.status === 'error') {
          throw new Error(result?.error ?? 'Upload failed');
        }
        return this.mapDocument(result.document!);
      }),
      map((doc) => this.enrichWithRequestMetadata(doc, request.metadata, request.file)),
    );
  }

  // ── Update (file + metadata) ─────────────────────────────────────────────

  /** Update an existing document (optionally replace the file). */
  updateDocument(request: IKbUpdateRequest): Observable<IKbDocument> {
    if (this.useMockData) {
      return of({
        document_id: request.documentId,
        name: `projects/demo/documents/${request.documentId}`,
        gcs_uri: `gs://volta-demo-kb/updated-file`,
        mime_type: request.file?.type ?? null,
        metadata: {
          filename: request.file?.name ?? null,
          file_size: request.file?.size ?? null,
          upload_date: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          document_type: request.metadata.document_type,
          tags: request.metadata.tags,
          user_comment: request.metadata.user_comment,
        },
        index_status: {
          status: KbDocumentStatus.INDEXED,
          index_time: new Date().toISOString(),
        },
      } as IKbApiDocumentDto).pipe(
        delay(1500),
        map((doc) => this.mapDocument(doc)),
      );
    }

    const formData = new FormData();
    if (request.file) {
      formData.append('file', request.file);
    }
    formData.append('metadata', JSON.stringify(request.metadata));

    return this.http.put<IKbApiResponse<IKbApiDocumentDto>>(`${this.baseUrl}/${request.documentId}`, formData).pipe(
      map((response) => this.mapDocument(response.data)),
      map((doc) => this.enrichWithRequestMetadata(doc, request.metadata, request.file)),
    );
  }

  // ── Delete ───────────────────────────────────────────────────────────────

  /** Delete documents by IDs (bulk). */
  deleteDocuments(documentIds: string[]): Observable<void> {
    return this.http.post<IKbApiResponse<IKbDeleteDocumentDataDto>>(`${this.baseUrl}/delete`, { document_ids: documentIds }).pipe(map(() => void 0));
  }

  // ── Download ─────────────────────────────────────────────────────────────

  /** Download documents: single → raw file, multiple → ZIP archive. */
  downloadDocuments(documentIds: string[]): Observable<Blob> {
    return this.http.post(`${this.baseUrl}/download`, { document_ids: documentIds }, { responseType: 'blob' });
  }

  /** Trigger a browser download for a blob. */
  triggerBlobDownload(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  // ── Mapping helpers ───────────────────────────────────────────────────────

  private mapDocumentsResponse(response: IKbApiResponse<IKbListDocumentsDataDto>): IKbDocument[] {
    return response.data?.documents?.map((document) => this.mapDocument(document)) ?? [];
  }

  /**
   * Resolve the full classification list from API metadata, preferring the
   * `document_types` array and falling back to the single `document_type`
   * (until the ATO API persists/returns the array). Always non-empty.
   */
  private resolveDocumentTypes(types?: KbDocumentType[], primary?: KbDocumentType): KbDocumentType[] {
    if (types?.length) return types;
    return [primary ?? KbDocumentType.GENERAL];
  }

  private mapDocument(document: IKbApiDocumentDto): IKbDocument {
    const metadata = document.metadata ?? {};
    const indexStatus = document.index_status ?? { status: KbDocumentStatus.PROCESSING };
    const types = this.resolveDocumentTypes(metadata.document_types, metadata.document_type);

    return {
      id: document.document_id,
      fileName: metadata.filename || this.extractFilename(document.name),
      type: types[0],
      types,
      status: indexStatus.status ?? KbDocumentStatus.UNKNOWN,
      uploadDate: metadata.upload_date ?? null,
      updatedAt: metadata.updated_at ?? null,
      aiOverview: null,
      userComment: metadata.user_comment ?? null,
      tags: metadata.tags ?? [],
      brands: metadata.brands ?? [],
      scopeLevel: metadata.scope_level ?? KbScopeLevel.GENERAL,
      language: metadata.language ?? null,
      isConfidential: metadata.is_confidential ?? false,
      mimeType: document.mime_type ?? null,
      gcsUri: document.gcs_uri,
      uploadedBy: metadata.uploaded_by ?? null,
      updatedBy: metadata.updated_by ?? null,
      indexTime: indexStatus.index_time ?? null,
      customerScope: this.mapCustomerScope(metadata.customer_scope),
      productScope: this.mapProductScope(metadata.product_scope),
    };
  }

  private mapCustomerScope(raw?: IKbDocumentMetadataDto['customer_scope']): IKbCustomerScope | null {
    if (!raw?.scope || raw.scope === 'all') return null;
    return {
      scope: raw.scope as IKbCustomerScope['scope'],
      companyIds: raw.company_ids ?? [],
      groupIds: raw.group_ids ?? [],
    };
  }

  private mapProductScope(raw?: IKbDocumentMetadataDto['product_scope']): IKbProductScope | null {
    if (!raw?.scope || raw.scope === 'all') return null;
    return {
      scope: raw.scope as IKbProductScope['scope'],
      brandIds: raw.brand_ids ?? [],
      categoryIds: raw.category_ids ?? [],
      labelIds: raw.label_ids ?? [],
      productIds: raw.product_ids ?? [],
    };
  }

  /**
   * Fill in any fields the API didn't return by using the request metadata
   * and file info we already have. Ensures the store always gets a complete IKbDocument
   * regardless of how minimal the API upload/update response is.
   */
  private enrichWithRequestMetadata(doc: IKbDocument, meta: IKbRequestMetadata, file?: File): IKbDocument {
    const types = doc.types?.length ? doc.types : this.resolveDocumentTypes(meta.document_types, meta.document_type);
    return {
      ...doc,
      fileName: doc.fileName || file?.name || doc.id,
      type: doc.type || types[0],
      types,
      tags: doc.tags?.length ? doc.tags : (meta.tags ?? []),
      userComment: doc.userComment ?? meta.user_comment ?? null,
      mimeType: doc.mimeType || file?.type || null,
      customerScope: doc.customerScope ?? this.mapCustomerScope(meta.customer_scope as any),
      productScope: doc.productScope ?? this.mapProductScope(meta.product_scope as any),
    };
  }

  private extractFilename(resourceName: string): string {
    const parts = resourceName.split('/');
    return parts[parts.length - 1] || resourceName;
  }

  private createMockListResponse(): IKbApiResponse<IKbListDocumentsDataDto> {
    return {
      success: true,
      data: {
        total: 2,
        documents: [
          {
            document_id: 'a1b2c3d4',
            name: 'projects/demo/documents/a1b2c3d4',
            gcs_uri: 'gs://volta-demo-kb/politique-livraison.pdf',
            mime_type: 'application/pdf',
            metadata: {
              environment_id: 'env-demo',
              filename: 'politique-livraison.pdf',
              file_size: 245000,
              upload_date: '2026-03-28T10:30:00Z',
              updated_at: '2026-03-28T10:30:00Z',
              document_type: KbDocumentType.SHIPPING_POLICY,
              tags: ['livraison', 'belgique'],
              brands: ['Equilibra'],
              scope_level: KbScopeLevel.GENERAL,
              language: 'fr',
              is_confidential: false,
              uploaded_by: 'Admin <admin@wolsal.com>',
              updated_by: 'Admin <admin@wolsal.com>',
              user_comment: 'Rules shared with customers for Belgian deliveries.',
            },
            index_status: {
              status: KbDocumentStatus.INDEXED,
              index_time: '2026-03-28T10:31:00Z',
            },
          },
          {
            document_id: 'e5f6g7h8',
            name: 'projects/demo/documents/e5f6g7h8',
            gcs_uri: 'gs://volta-demo-kb/catalogue-printemps.xlsx',
            mime_type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            metadata: {
              environment_id: 'env-demo',
              filename: 'catalogue-printemps.xlsx',
              file_size: 560120,
              upload_date: '2026-03-29T08:15:00Z',
              updated_at: '2026-03-29T08:20:00Z',
              document_type: KbDocumentType.PRODUCT_CATALOG,
              tags: ['catalogue', 'printemps'],
              brands: ['Equilibra', 'Volta'],
              scope_level: KbScopeLevel.PRODUCT,
              language: 'fr',
              is_confidential: true,
              uploaded_by: 'Sarah <sarah@wolsal.com>',
              updated_by: 'Sarah <sarah@wolsal.com>',
              user_comment: 'Contains the latest seasonal assortment.',
            },
            index_status: {
              status: KbDocumentStatus.PROCESSING,
            },
          },
        ],
      },
      message: 'Documents loaded',
    };
  }
}
